import { Injectable, Injector } from '@angular/core';
import { BadgeApi } from '../api/badge.api';
import { ExternalActivityApi } from '../api/external-activity.api';
import { ExternalCoachingVideoApi } from '../api/external-coaching-video.api';
import { ExternalContributionApi } from '../api/external-contribution.api';
import { ExternalPlaylistApi } from '../api/external-playlist.api';
import { ExternalStatsApi } from '../api/external-stats.api';
import { ExternalUserApi } from '../api/external-user.api';
import { ExternalVideoApi } from '../api/external-video.api';
import { ExternalWorkoutApi } from '../api/external-workout.api';
import { ActivitiesByMonthFacade } from './activities-by-month.facade';
import { ActivitiesFacade } from './activities.facade';
import { BadgesFacade } from './badges.facade';
import { CoachingVideosFacade } from './coaching-videos.facade';
import { ContributionFacade } from './contribution.facade';
import { AVAILABLE_FACADE } from './facades.registry';
import { PlaylistsFacade } from './playlists.facade';
import { StatsFacade } from './stats.facade';
import { UserFacade } from './user.facade';
import { VideosFacade } from './videos.facade';
import { WorkoutsFacade } from './workouts.facade';
import { TranslateService } from '@ngx-translate/core';
import { ToasterService } from 'app/services/toaster.service';

interface CreateOptions {
  [key: string]: any;
}

@Injectable({
  providedIn: 'root',
})
export class FacadeFactory {
  constructor(
    private readonly injector: Injector,
    private toasterService: ToasterService,
    private translate: TranslateService
  ) { }

  create<T>(type: string, options: any): T | null {
    switch (type) {
      case AVAILABLE_FACADE.USER_FACADE:
        return this.createUserFacade(options) as any as T;
      case AVAILABLE_FACADE.ACTIVITIES_FACADE:
        return this.createActivitiesFacade(options) as any as T;
      case AVAILABLE_FACADE.ACTIVITIES_BY_MONTH_FACADE:
        return this.createActivitiesByMonthFacade(options) as any as T;
      case AVAILABLE_FACADE.PLAYLISTS_FACADE:
        return this.createPlaylistsFacade(options) as any as T;
      case AVAILABLE_FACADE.VIDEOS_FACADE:
        return this.createVideosFacade(options) as any as T;
      case AVAILABLE_FACADE.COACHING_VIDEOS_FACADE:
        return this.createCoachingVideosFacade(options) as any as T;
      case AVAILABLE_FACADE.WORKOUTS_FACADE:
        return this.createWorkoutsFacade(options) as any as T;
      case AVAILABLE_FACADE.CONTRIBUTION_FACADE:
        return this.createContributionFacade(options) as any as T;
      case AVAILABLE_FACADE.BADGES_FACADE:
        return this.createBadgeFacade() as any as T;
      case AVAILABLE_FACADE.STATS_FACADE:
        return this.createStatsFacade(options) as any as T;
      default:
        console.warn(`Unable to create facade: ${type}`);
        return null;
    }
  }

  private createUserFacade(options: CreateOptions): UserFacade {
    const userApi: ExternalUserApi = this.injector.get(ExternalUserApi);
    return new UserFacade(userApi, options.id);
  }

  private createWorkoutsFacade(options: any): WorkoutsFacade {
    const workoutApi: ExternalWorkoutApi =
      this.injector.get(ExternalWorkoutApi);
    return new WorkoutsFacade(
      workoutApi,
      options.id,
      this.toasterService,
      this.translate
    );
  }

  private createActivitiesFacade(options: CreateOptions): ActivitiesFacade {
    const activitiesApi: ExternalActivityApi =
      this.injector.get(ExternalActivityApi);
    return new ActivitiesFacade(activitiesApi, options.id);
  }

  private createActivitiesByMonthFacade(
    options: CreateOptions
  ): ActivitiesByMonthFacade {
    const activitiesApi: ExternalActivityApi =
      this.injector.get(ExternalActivityApi);
    return new ActivitiesByMonthFacade(activitiesApi, options.id);
  }

  private createVideosFacade(options: CreateOptions): VideosFacade {
    const videosApi: ExternalVideoApi = this.injector.get(ExternalVideoApi);
    return new VideosFacade(videosApi, options.id, this.toasterService, this.translate);
  }

  private createCoachingVideosFacade(
    options: CreateOptions
  ): CoachingVideosFacade {
    const coachingVideosApi: ExternalCoachingVideoApi = this.injector.get(
      ExternalCoachingVideoApi
    );
    return new CoachingVideosFacade(coachingVideosApi, options.id, this.toasterService);
  }

  private createPlaylistsFacade(options: CreateOptions): PlaylistsFacade {
    const playlistApi: ExternalPlaylistApi =
      this.injector.get(ExternalPlaylistApi);
    return new PlaylistsFacade(
      playlistApi,
      options.id,
      this.toasterService,
      this.translate
    );
  }

  private createBadgeFacade(): BadgesFacade {
    const badgeApi: BadgeApi = this.injector.get(BadgeApi);
    return new BadgesFacade(badgeApi);
  }

  private createContributionFacade(options: CreateOptions): ContributionFacade {
    const contributionApi: ExternalContributionApi = this.injector.get(
      ExternalContributionApi
    );
    return new ContributionFacade(contributionApi, options.id);
  }

  private createStatsFacade(options: CreateOptions): StatsFacade {
    const statsApi: ExternalStatsApi = this.injector.get(ExternalStatsApi);
    return new StatsFacade(statsApi, options.id);
  }
}
